import { create } from 'zustand';
import { fetchDictItems } from '@/service/api/dict';

interface DictState {
  /** 字典缓存 Map（Key: typeCode, Value: 字典项列表） */
  dictMap: Record<string, Api.Dict.PublicDictItemResponse[]>;
  /** 各 typeCode 的加载状态 */
  loadingMap: Record<string, boolean>;
  /** 各 typeCode 的错误信息 */
  errorMap: Record<string, string | null>;

  /**
   * 获取指定 typeCode 的字典项列表 (带全局内存缓存与并发去重)
   */
  fetchDict: (typeCode: string) => Promise<Api.Dict.PublicDictItemResponse[]>;

  /**
   * 同步获取当前缓存中的字典项列表
   */
  getDictItems: (typeCode: string) => Api.Dict.PublicDictItemResponse[];

  /**
   * 根据 itemCode 获取字典显示的 item_name
   */
  getDictLabel: (typeCode: string, itemCode?: string, fallback?: string) => string;

  /**
   * 根据 itemCode 获取字典的 item_value
   */
  getDictValue: (typeCode: string, itemCode?: string, fallback?: string) => string;

  /**
   * 根据 itemCode 获取完整的字典项对象
   */
  getDictItem: (
    typeCode: string,
    itemCode?: string
  ) => Api.Dict.PublicDictItemResponse | undefined;

  /**
   * 清除字典缓存
   */
  clearDictCache: (typeCode?: string) => void;
}

/** 缓存运行中的请求 Promise，防止同时间多次发起相同 typeCode 请求 */
const fetchDictPromises: Record<
  string,
  Promise<Api.Dict.PublicDictItemResponse[]> | undefined
> = {};

export const useDictStore = create<DictState>((set, get) => ({
  dictMap: {},
  loadingMap: {},
  errorMap: {},

  fetchDict: async (typeCode: string) => {
    if (!typeCode) return [];

    const { dictMap } = get();
    // 1. 已有缓存，直接返回
    if (dictMap[typeCode]) {
      return dictMap[typeCode];
    }

    // 2. 正在请求中，返回已有 Promise
    if (fetchDictPromises[typeCode]) {
      return fetchDictPromises[typeCode]!;
    }

    // 3. 设置加载状态并发起 API 请求
    set((state) => ({
      loadingMap: { ...state.loadingMap, [typeCode]: true },
      errorMap: { ...state.errorMap, [typeCode]: null }
    }));

    const promise = fetchDictItems(typeCode)
      .then((res) => {
        const items = res?.items || [];
        set((state) => ({
          dictMap: { ...state.dictMap, [typeCode]: items },
          loadingMap: { ...state.loadingMap, [typeCode]: false },
          errorMap: { ...state.errorMap, [typeCode]: null }
        }));
        return items;
      })
      .catch((err) => {
        const errorMessage =
          err instanceof Error ? err.message : `获取字典 [${typeCode}] 失败`;
        set((state) => ({
          loadingMap: { ...state.loadingMap, [typeCode]: false },
          errorMap: { ...state.errorMap, [typeCode]: errorMessage }
        }));
        throw err;
      })
      .finally(() => {
        delete fetchDictPromises[typeCode];
      });

    fetchDictPromises[typeCode] = promise;
    return promise;
  },

  getDictItems: (typeCode: string) => {
    if (!typeCode) return [];
    return get().dictMap[typeCode] || [];
  },

  getDictLabel: (typeCode: string, itemCode?: string, fallback = '') => {
    if (!typeCode || itemCode === undefined || itemCode === null || itemCode === '') {
      return fallback;
    }
    const items = get().dictMap[typeCode] || [];
    const target = items.find((item) => item.item_code === itemCode);
    return target?.item_name || fallback || itemCode;
  },

  getDictValue: (typeCode: string, itemCode?: string, fallback = '') => {
    if (!typeCode || itemCode === undefined || itemCode === null || itemCode === '') {
      return fallback;
    }
    const items = get().dictMap[typeCode] || [];
    const target = items.find((item) => item.item_code === itemCode);
    return target?.item_value || fallback;
  },

  getDictItem: (typeCode: string, itemCode?: string) => {
    if (!typeCode || itemCode === undefined || itemCode === null || itemCode === '') {
      return undefined;
    }
    const items = get().dictMap[typeCode] || [];
    return items.find((item) => item.item_code === itemCode);
  },

  clearDictCache: (typeCode?: string) => {
    if (!typeCode) {
      set({ dictMap: {}, loadingMap: {}, errorMap: {} });
    } else {
      set((state) => {
        const nextDictMap = { ...state.dictMap };
        const nextLoadingMap = { ...state.loadingMap };
        const nextErrorMap = { ...state.errorMap };
        delete nextDictMap[typeCode];
        delete nextLoadingMap[typeCode];
        delete nextErrorMap[typeCode];
        return {
          dictMap: nextDictMap,
          loadingMap: nextLoadingMap,
          errorMap: nextErrorMap
        };
      });
    }
  }
}));
